import React from 'react'
import PropTypes from 'prop-types'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

const ImageSrcFormat = (src, width, height, quality, templates, fallbackFmt) => {
  const supportedResolutions = [1, 2]
  let srcSet = ''
  const templatesParam = templates.length ? `${templates.join('&')}&` : ``
  supportedResolutions.forEach((res) => {
    const widthParam = typeof width !== 'undefined' ? `w=${width * res}&` : ``
    const heightParam = typeof height !== 'undefined' ? `h=${height * res}&` : ``
    srcSet += `${src}?${templatesParam}${widthParam}${heightParam}qlt=${quality}&fmt=webp ${res}x, `
  })
  const widthParam = typeof width !== 'undefined' ? `w=${width}&` : ``
  const heightParam = typeof height !== 'undefined' ? `h=${height}&` : ``
  return {
    src: `${src}?${templatesParam}${widthParam}${heightParam}qlt=${quality}&${fallbackFmt}`,
    srcSet
  }
}

const LazyLoadWrapper = ({ lazy, lazyOptions, children }) => {
  if (lazy) {
    const { afterLoad, beforeLoad, visibleByDefault, scrollPosition } = lazyOptions
    return (
      <LazyLoadComponent
        afterLoad={afterLoad}
        beforeLoad={beforeLoad}
        visibleByDefault={visibleByDefault}
        scrollPosition={scrollPosition}>
        {children}
      </LazyLoadComponent>
    )
  }
  return children
}

const Image = ({
  src,
  alt,
  width,
  height,
  quality,
  templates,
  responsive,
  lazy,
  lazyOptions,
  className,
  fallbackFmt,
  'data-test': dataTest,
  ...props
}) => {
  const { src: imageSrc, srcSet } = ImageSrcFormat(src, width, height, quality, templates, fallbackFmt)
  return (
    <LazyLoadWrapper lazy={lazy} lazyOptions={lazyOptions}>
      <picture>
        <source type='image/webp' srcSet={srcSet} />
        <source type='image/pjpeg' srcSet={imageSrc} />
        <img
          src={imageSrc}
          width={responsive ? '100%' : width}
          height={responsive ? '100%' : height}
          alt={alt}
          data-test={dataTest}
          className={className}
          {...props}
        />
      </picture>
    </LazyLoadWrapper>
  )
}

Image.propTypes = {
  /**
   * Required prop for the Image src
   */
  src: PropTypes.string.isRequired,

  /**
   * Required prop for the Image alt text
   */
  alt: PropTypes.string.isRequired,

  /**
   * Prop for the Image width
   */
  width: PropTypes.number,

  /**
   * Prop for the Image height
   */
  height: PropTypes.number,

  /**
   * Optional prop for the Image quality
   */
  quality: PropTypes.number,

  /**
   * Optional prop for the fallback format of an image (not webp)
   */
  fallbackFmt: PropTypes.string,

  /**
   * Specify an optional Amplience templates array to be added to the request as POST param
   */
  templates: PropTypes.array,

  /**
   * Specify whether the Image should only load when in viewport
   */
  lazy: PropTypes.bool,

  /**
   * Specify whether the Image should only load when in viewport
   */
  lazyOptions: PropTypes.shape({
    afterLoad: PropTypes.func,
    beforeLoad: PropTypes.func,
    visibleByDefault: PropTypes.bool,
    scrollPosition: PropTypes.object
  }),

  /**
   * Specify whether the Image should have 100% height and width
   */
  responsive: PropTypes.bool,

  /**
   * Specify an optional className to be added to your Image
   */
  className: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added to your Image for e2e testing
   */
  'data-test': PropTypes.string
}

Image.defaultProps = {
  quality: 75,
  templates: [],
  lazy: false,
  responsive: false,
  lazyOptions: {
    afterLoad: /* istanbul ignore next */ () => {},
    beforeLoad: /* istanbul ignore next */ () => {},
    visibleByDefault: false,
    // eslint-disable-next-line id-length
    scrollPosition: { x: 0, y: 0 }
  },
  fallbackFmt: 'fmt.jpeg.interlaced=true',
  'data-test': 'component-image'
}

export default Image
